/* eslint-disable arrow-body-style */
import * as React from "react";
import { connect } from "react-redux";
import Table from "../../components/Table/Table";
import { CellInfo } from "../../components/Table/Migration/LegacyTypes";
import MenuButton from "../../components/MenuButton/MenuButton";
import { Modal } from "semantic-ui-react";
import { pdApiUrl } from "../../utils/pdApi";
import axios, { AxiosResponse } from "axios";
import _ from "lodash";

type StateProps = {
  editedItem: Api.AnyItem;
  columns: State.Column[];
  pdApi: State.PdApi;
  isShortenTextEnabled: boolean;
  locsOriginal: Api.AnyItem;
};

type OwnProps = {
  toggleEditLocalizationStatusModal: () => void;
  setLocalizationsEdited: Api.AnyItem;
  setLocalizationsNonEdited: Api.AnyItem;
  itemType: string;
  userPermissions: boolean

  // Name of the endpoint in pd-api that is used to fetch localizations to the item
  getOption?: string;

  // Flag to indicate that english translations have been changed already in parent
  englishTranslationChanged?: boolean;
};

export type EditLocalizationStatusProps = StateProps & OwnProps;

export type EditLocalizationStatusState = {
  editedItems: Api.AnyLocs[];
  originalItems: Api.AnyLocs[]; 
  isLoading?: boolean;
};

export class EditLocalizationStatus extends React.Component<
  EditLocalizationStatusProps,
  EditLocalizationStatusState
> {
  private emptyTextPlaceholder = "";
  constructor(props: EditLocalizationStatusProps) {
    super(props);
    this.state = {
      editedItems: [],
      originalItems: []
    };
  }

  public async componentDidMount(): Promise<void> {
    // Fetch localizations from PDAPI
    let storedItem: Api.AnyItem;
    if (this.props.editedItem.id && this.props.locsOriginal?.length === 0 || this.props.locsOriginal === undefined) {
      const url = `${pdApiUrl}/${this.props.getOption}/${this.props.editedItem.id}`;
      this.setState(prevState => ({
        ...prevState,
        isLoading: true
      }));
      const response: void | AxiosResponse<typeof storedItem> = await axios
        .get(url)
        .catch(e => {
          this.setState(prevState => ({
            ...prevState,
            errorMessage: "Error fetching localizations: " + e,
            fetchingAuxData: false,
            isLoading: false
          }));
        });
        if (response) {
          storedItem = (response as AxiosResponse<Promise<void>>)?.data || undefined;
          this.setState(prevState => ({
            ...prevState,
            isLoading: false
          }));
        }
    }
    const storedLocalizations = storedItem?.locs || this.props.locsOriginal || [];

    // Combine the newly fetched locs to the locs being previously edited
    const combinedLocs = [
      ...Array.from(new Set([...storedLocalizations, ...this.props.editedItem.locs]))
    ].map((itm: Api.AnyLocs) => {
      const editedLocs = this.props.editedItem.locs as Api.AnyLocs[];
      return (
        editedLocs?.find(a => a.localeNavigation.locale === itm.localeNavigation.locale) ?? itm
      );
    });
    // Create rows to the table
    const populatedLocales = this.createLocaleRows(
      combinedLocs,
      this.props.englishTranslationChanged
    );

    this.setState(prevstate => ({
      ...prevstate,
      editedItems: populatedLocales,
      originalItems: storedLocalizations
    }));
  }

  private createLocaleRows = (
    localizations: Api.AnyLocs[],
    englishTranslationChanged?: boolean
  ): Api.AnyLocs[] => {
    // ...returns any[] on purpose!

    return this.props.pdApi.locales.map(locale => {
      const editedLocalization = localizations.find(localization => {
        return localization?.localeNavigation.locale === locale.locale;
      });
      const isNonEmpty =
        editedLocalization &&
        (!!editedLocalization.text ||
          !!(editedLocalization as Api.LocsEnumeration).description ||
          !!(editedLocalization as Api.LocsFault).reason ||
          !!(editedLocalization as Api.LocsFault).action);

      // no need to set update flag for en-US locale, only other locales
      const needsUpdateFlagApplicable =
        isNonEmpty &&
        !!editedLocalization &&
        editedLocalization.localeNavigation.locale !== "en-US";

      return {
        enumItemId: undefined,
        text: editedLocalization?.text || this.emptyTextPlaceholder,
        reason: (editedLocalization as Api.LocsFault)?.reason || this.emptyTextPlaceholder,
        action: (editedLocalization as Api.LocsFault)?.action || this.emptyTextPlaceholder,
        description:
          (editedLocalization as Api.LocsEnumeration)?.description || this.emptyTextPlaceholder,
        needsUpdate:
          editedLocalization?.needsUpdate ||
          (needsUpdateFlagApplicable && englishTranslationChanged) ||
          false,
        localeNavigation: locale
      };
    });
  };

  // Table cell edit handler
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onBlur = (e: any, cellInfo: CellInfo) => {
    const columnName: string = cellInfo.column.headerClassName as keyof Api.AnyLocs;
    const newValue = e.target.innerText;

    // First inspect changes in english localization...
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const originalEnglishLocalization: any = this.state.originalItems.find(
      loc => loc.localeNavigation.locale === "en-US"
    );
    const englishBlurred = cellInfo.row.Locale === "en-US";
    let englishDifferent = false;
    if (englishBlurred) {
      if (Object.keys(this.columnNameMapping).includes(columnName)) {
        englishDifferent = Object.keys(this.columnNameMapping).some(propertyName => {
          if (propertyName === columnName) {
            const retval = originalEnglishLocalization
              ? originalEnglishLocalization[propertyName] !== newValue
              : true;
            return retval;
          } else {
            const retval = originalEnglishLocalization
              ? originalEnglishLocalization[propertyName] !==
              cellInfo.row[this.columnNameMapping[propertyName]]
              : true;
            return retval;
          }
        });
      }
    }
    // ...then process all ather localizations accordingly
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const list = this.state.editedItems.map((edited: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const original: any = this.state.originalItems.find(
        original => edited.localeNavigation.locale === original.localeNavigation.locale
      );

      // Note: do not touch 'needsUpdate' if it's alredy set
      // - set only those locales that has the blurred info previously translated
      // - "en-US"-locale shall not be affected, ever!
      if (
        !!edited[columnName] &&
        !(original?.needsUpdate || false) &&
        englishBlurred &&
        (edited as Api.AnyLocs).localeNavigation.locale !== "en-US"
      ) {
        edited.needsUpdate = englishDifferent;
      }
      if (!!columnName && edited.localeNavigation.locale === cellInfo.row.Locale) {
        edited[columnName] = newValue;
      }
      return edited;
    }, cellInfo);

    this.setState(prevState => {
      return { ...prevState, editedItems: [...list] };
    });
  };

  private isLocsEmpty(item: Api.AnyLocs): boolean {
    if (!item) {
      return false;
    }
    return (
      item.text === this.emptyTextPlaceholder &&
      (item as Api.LocsEnumeration).description === this.emptyTextPlaceholder &&
      (item as Api.LocsFault).action === this.emptyTextPlaceholder &&
      (item as Api.LocsFault).reason === this.emptyTextPlaceholder
    );
  }

  private notEmptyOrMarkedForDeletion(item: Api.AnyLocs, originalItem?: Api.AnyLocs): boolean {
    const itemEmpty = this.isLocsEmpty(item);
    const originalItemEmpty = originalItem ? this.isLocsEmpty(originalItem) : true;
    const markedForDeletion = itemEmpty && !originalItemEmpty;
    return !this.isLocsEmpty(item) || markedForDeletion;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private saveAndClose = (): any => {
    this.props.setLocalizationsEdited(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      this.state.editedItems.filter((editedItem) => {
        const originalItem = this.state.originalItems.find(
          itm => itm.localeNavigation.locale === editedItem.localeNavigation.locale
        );
        return this.notEmptyOrMarkedForDeletion(editedItem, originalItem);
      }),this.state.originalItems
    );
    this.props.toggleEditLocalizationStatusModal();
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private Close = (): any => {
        if(this.state.originalItems.length !== 0){
                this.props.setLocalizationsNonEdited(this.state.originalItems);
        }
        this.props.toggleEditLocalizationStatusModal();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setBlurHandlers = (columns: State.Column[]): any => {
    const columnsWithHandlers = columns.map(col => {
      if (col.headerClassName) {
        // eslint-disable-next-line react/display-name
        col.Cell = (cellInfo: CellInfo) => (
          <div
            className="editable-cell-text"
            contentEditable={this.props.userPermissions}
            suppressContentEditableWarning
            onBlur={e => this.onBlur(e, cellInfo)}
          >
            {String(cellInfo.value)}
          </div>
        );
      }
      return col;
    });
    return columnsWithHandlers;
  };

  // Mapping from localization item property name to table column name
  private columnNameMapping: { [index: string]: string } = {
    text: "UI Name",
    description: "Description",
    action: "Action",
    reason: "Reason"
  };

  public render(): JSX.Element {
    const { columns, itemType, isShortenTextEnabled,userPermissions } = this.props;
    const { editedItems, originalItems, isLoading } = this.state;
    const isEdited = this.locsChanged(editedItems, originalItems) && userPermissions;
    return (
      <>
        <div className="modal-header">
          <p className="title">{itemType + " localization status"}</p>
          <div className="edit-header-actions">
            <MenuButton type="Save" handleOnClick={this.saveAndClose} disabled={!isEdited} />
            <MenuButton
              type="Cancel"
              handleOnClick={this.Close}
            />
          </div>
        </div>
        <Modal.Content scrolling={true}>
          <div className="flex-1">
            <Table
              rows={this.state.editedItems}
              loading={isLoading}
              loadingText="Fetching Localizations"
              columns={this.setBlurHandlers(columns)}
              isHidingTextActive={isShortenTextEnabled}
              handleRowSelection={() => {
                /*  */
              }}
              isRowSelected={() => null}
              pageSize={10000}
              disablePagination={true}
            />
          </div>
        </Modal.Content>
      </>
    );
  }

  private locsChanged = (editedItems: Api.AnyLocs[], originalItems: Api.AnyLocs[]): boolean => {
    const editedItemsChanged = editedItems.some(editedItem => {
      const originalItem = originalItems.find(o => o.localeNavigation.locale === editedItem.localeNavigation.locale);
      // Check if Locs has been added or changed
      if (editedItem.text
        || (editedItem as Api.LocsDriveSignal).description
        //|| (editedItem as Api.LocsEscalatorRTD).description
        || (editedItem as Api.LocsFault).reason
        || (editedItem as Api.LocsFault).action) {
        if (originalItem) {
          // originalitem was found => Loc was edited
          const fieldsInEdited = {
            text: editedItem.text,
            description: (editedItem as Api.LocsDriveTest).description,
            reason: (editedItem as Api.LocsFault).reason,
            action: (editedItem as Api.LocsFault).action
          };
          const fieldsInOriginal = {
            text: originalItem.text,
            description: ((originalItem as Api.LocsDriveTest).description || ""),
            reason: ((originalItem as Api.LocsFault).reason || ""),
            action: ((originalItem as Api.LocsFault).action || "")
          };
          return !_.isEqual(fieldsInEdited, fieldsInOriginal);
        }
        else {
          // originalitem was not found => Loc was added
          return true;
        }
      }
      else {
        // check if any Locs has been removed
        if (originalItem) {
          const originalHasAValue = !!originalItem.text
            || !!(originalItem as Api.LocsDriveTest).description
            || !!(originalItem as Api.LocsFault).reason
            || !!(originalItem as Api.LocsFault).action;
          return originalHasAValue;
        }
      }
    });

    return editedItemsChanged;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedItem: state.editView.editedObject,
  pdApi: state.pdApi,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

export default connect(mapStateToProps)(EditLocalizationStatus);
